import { useMemo, useState, useCallback } from 'react';
import { useAtomValue } from 'jotai';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { SearchList } from '../../../../shared/SearchList';
import {
  allAssemblyTemplates,
  selectedTemplateFiltersState,
  assemblyTemplate as assemblyTemplateState,
} from '@/components/Analysis/state';
import { FiltersCount } from '../styles';
import { useGroupFilterValue } from '@/hooks';
import { useUnitConversion } from '@/hooks/useUnitConversion';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { TemplateFieldTypeEnum, UnitsEnum } from '@/__generated__/graphql';
import { drawerBackground } from '@/theme/colors';
import { structurePipesByStructureId } from '@/components/Analysis/state/atoms';

type TabPanelProps = {
  children?: React.ReactNode;
  show: boolean;
};

const FilterTabpanel = (props: TabPanelProps) => {
  const { children, show } = props;

  return (
    <Stack role="tabpanel" hidden={!show}>
      {show && (
        <Stack>
          <Typography>{children}</Typography>
        </Stack>
      )}
    </Stack>
  );
};

type CustomAccordianProps = {
  expanded: string[];
  name: string;
  id: string;
  serverSide: boolean;
  templateId: string;
  templateFieldId?: string;
  templateFieldKey: string;
  unit?: UnitsEnum | null;
  displayUnit?: UnitsEnum | null;
  handleChange: (() => void) | undefined;
  structurePipes?: { [key: string]: number } | null;
};
const CustomAccordian = ({
  expanded,
  name,
  id,
  serverSide,
  templateId,
  templateFieldId,
  handleChange,
  templateFieldKey,
  unit,
  displayUnit,
  structurePipes,
}: CustomAccordianProps) => {
  const isFilterExpanded = expanded.includes(id);
  const selectedTemplateFilters = useAtomValue(selectedTemplateFiltersState);
  const selected = useGroupFilterValue(selectedTemplateFilters);

  return (
    <Accordion
      sx={{
        margin: 0,
        boxShadow: 'none',
        backgroundColor: drawerBackground,
        '.Mui-expanded&:before': { opacity: 1 },
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        '@media(max-width: 1728px)': {
          '& .MuiAccordionSummary-root': {
            minHeight: '32px',
          },
        },
      }}
      expanded={isFilterExpanded}
      onChange={handleChange}
      key={`${name}-${id}`}
      disableGutters
    >
      <AccordionSummary
        sx={{ padding: '0', backgroundColor: drawerBackground, m: 0 }}
        expandIcon={<ArrowDropDownIcon />}
        aria-controls="panel1a-content"
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography sx={{ fontWeight: 500, paddingLeft: '1rem' }} variant="body2">
            {name}
          </Typography>
          {selected && selected[templateFieldKey]?.length > 0 && (
            <FiltersCount>{selected[templateFieldKey]?.length}</FiltersCount>
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          margin: 0,
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        <SearchList
          isExpanded={isFilterExpanded}
          templateId={templateId}
          templateFieldId={templateFieldId}
          templateFieldKey={templateFieldKey}
          name={name}
          serverSide={serverSide}
          selected={selected}
          unit={unit}
          displayUnit={displayUnit}
          structurePipes={structurePipes}
        />
      </AccordionDetails>
    </Accordion>
  );
};

export const AssemblyFilters = () => {
  const [expanded, setExpanded] = useState<string[]>([]);
  const allTemplates = useAtomValue(allAssemblyTemplates);
  const assemblyTemplate = useAtomValue(assemblyTemplateState);
  const { getUnitSuffix } = useUnitConversion();
  const structureId = useGetStructureId();
  const structurePipesByStructure = useAtomValue(structurePipesByStructureId);
  const structurePipes = structureId ? structurePipesByStructure[structureId] : null;

  const filters = useMemo(() => {
    return assemblyTemplate;
  }, [assemblyTemplate]);

  const handleChange = useCallback(
    (id: string) => () => {
      const updatedExpanded = expanded.includes(id)
        ? expanded.filter((element) => element !== id)
        : [...expanded, id];
      setExpanded(updatedExpanded);
    },
    [expanded]
  );

  const optionsList: { [key: string]: { id: string; serverSide: boolean } } = useMemo(
    () => ({}),
    []
  );

  filters?.fields.forEach((field) => {
    optionsList[field.name] = { id: field.id, serverSide: field.serverSide };
  });

  const AccordionMemo = useMemo(() => {
    if (allTemplates?.length === 1) {
      // Temporarily filter out INPUT type filters to cater for scenario for fields to show up in the equipment table but not filters
      return filters?.fields
        ?.filter((field) => {
          return (
            field.type !== TemplateFieldTypeEnum.Input &&
            field.type !== TemplateFieldTypeEnum.Date &&
            field.displayInQuery
          );
        })
        .map(({ name, id, serverSide, key, unit, displayUnit }) => {
          const unitForDisplay = displayUnit ?? unit;
          const showSuffix = unit !== null && displayUnit !== null;
          const displayName = showSuffix
            ? `${name} (${getUnitSuffix({ unit: unitForDisplay })})`
            : name;
          return (
            <CustomAccordian
              key={id}
              expanded={expanded}
              name={displayName}
              id={id}
              serverSide={serverSide}
              templateId={filters?.id}
              templateFieldId={id}
              templateFieldKey={key ?? ''}
              unit={unit}
              displayUnit={displayUnit}
              structurePipes={structurePipes}
              handleChange={handleChange(id)}
            />
          );
        });
    }

    return allTemplates?.map((template) => {
      return (
        <FilterTabpanel show key={template?.id}>
          {filters?.fields?.map(({ name, id, serverSide, key, unit, displayUnit }) => {
            const unitForDisplay = displayUnit ?? unit;
            const showSuffix = unit !== null && displayUnit !== null;
            const displayName = showSuffix
              ? `${name} (${getUnitSuffix({ unit: unitForDisplay })})`
              : name;
            return (
              <CustomAccordian
                key={id}
                expanded={expanded}
                name={displayName}
                id={id}
                serverSide={serverSide}
                templateId={template?.id}
                templateFieldId={id}
                templateFieldKey={key ?? ''}
                unit={unit}
                displayUnit={displayUnit}
                structurePipes={structurePipes}
                handleChange={handleChange(id)}
              />
            );
          })}
        </FilterTabpanel>
      );
    });
  }, [
    expanded,
    allTemplates,
    filters?.fields,
    filters?.id,
    handleChange,
    getUnitSuffix,
    structurePipes,
  ]);

  return (
    <Stack data-testid="template-filters-container">
      {AccordionMemo}
      <Divider />
    </Stack>
  );
};
